import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, Eye, FileText, Save, Mic, Info, ChevronRight, Plus, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, X } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const ESTADO_LABELS = { abierta: "Habilitada para alumnos", cerrada: "No habilitada" };

export default function EvaluacionOralDetalle() {
    const { assessmentId } = useParams();
    const navigate = useNavigate();

    const [assessment, setAssessment] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [contextoText, setContextoText] = useState("");
    const [tiempo, setTiempo] = useState(120);
    const [duracion, setDuracion] = useState(10);
    const [saving, setSaving] = useState(false);
    const [showNewParte, setShowNewParte] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [{ data: a }, { data: s }] = await Promise.all([
                api.get(`/oral-assessments/${assessmentId}`),
                api.get(`/oral-assessments/${assessmentId}/sessions`),
            ]);
            setAssessment(a);
            setContextoText(a.contexto_text || "");
            setTiempo(a.tiempo_por_respuesta_seg || 120);
            setDuracion(a.duracion_total_min || 10);
            setSessions(s || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error cargando la evaluación");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, [assessmentId]);

    async function saveConfig() {
        setSaving(true);
        try {
            await api.patch(`/oral-assessments/${assessmentId}`, {
                contexto_text: contextoText,
                tiempo_por_respuesta_seg: Number(tiempo) || 120,
                duracion_total_min: Number(duracion) || 10,
            });
            toast.success("Guardado");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error guardando");
        } finally {
            setSaving(false);
        }
    }

    async function uploadRubric(file) {
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/oral-assessments/${assessmentId}/rubric`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Rúbrica subida");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo la rúbrica");
        }
    }

    async function uploadContextoFile(file) {
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/oral-assessments/${assessmentId}/contexto`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Archivo de tema/extracto subido");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo el archivo");
        }
    }

    async function toggleEstado() {
        const nuevoEstado = assessment.estado === "abierta" ? "cerrada" : "abierta";
        try {
            await api.patch(`/oral-assessments/${assessmentId}`, { estado: nuevoEstado });
            toast.success(nuevoEstado === "abierta" ? "Habilitada para los alumnos" : "Deshabilitada");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Le falta la rúbrica o el tema para poder habilitarla");
        }
    }

    async function removeParte(parteId) {
        try {
            await api.delete(`/oral-assessments/${assessmentId}/partes/${parteId}`);
            toast.success("Parte eliminada");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error eliminando la parte");
        }
    }

    async function moveParte(partes, index, direction) {
        const target = index + direction;
        if (target < 0 || target >= partes.length) return;
        const reordered = [...partes];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        try {
            await api.patch(`/oral-assessments/${assessmentId}/partes/reorder`, { orden_ids: reordered.map((p) => p.id) });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error reordenando");
        }
    }

    async function deleteAssessment() {
        if (!window.confirm("¿Eliminar esta evaluación oral? También se borran las sesiones de práctica de los alumnos. Esta acción no se puede deshacer.")) return;
        try {
            await api.delete(`/oral-assessments/${assessmentId}`);
            toast.success("Evaluación eliminada");
            navigate("/profesor/evaluacion-oral");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error eliminando la evaluación");
        }
    }

    if (loading) return <div className="p-10 text-edu-muted">Cargando…</div>;
    if (!assessment) return <div className="p-10 text-edu-muted">No se pudo cargar.</div>;

    const sortedPartes = [...(assessment.partes || [])].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-2xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver
            </button>

            <div className="edu-card p-6 mb-4">
                <div className="flex items-start justify-between gap-4 mb-1">
                    <h1 className="text-xl font-semibold inline-flex items-center gap-2"><Mic size={20} className="text-edu-navy" /> {assessment.titulo}</h1>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={toggleEstado}
                            className={`text-xs px-3 py-1.5 rounded-lg border whitespace-nowrap ${assessment.estado === "abierta" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-edu text-edu-muted"}`}
                        >
                            {ESTADO_LABELS[assessment.estado]}
                        </button>
                        <button
                            onClick={deleteAssessment}
                            title="Eliminar evaluación oral"
                            className="p-2 rounded-lg border border-edu text-edu-muted hover:text-red-600 hover:border-red-300"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                </div>
                <p className="text-xs text-edu-muted">Los alumnos de este curso verán esta práctica en su sidebar solo cuando esté habilitada.</p>
            </div>

            <div className="edu-card p-6 mb-4 space-y-4">
                <div>
                    <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Tema / extracto (lo que verá la IA para generar las preguntas)</span>
                    <textarea value={contextoText} onChange={(e) => setContextoText(e.target.value)} rows={5} className="edu-input w-full" placeholder="Ej: tarjetas de tema de Cambridge Speaking, o el extracto de la obra literaria para el Individual Oral…" />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-edu hover:bg-edu-soft cursor-pointer">
                        <Upload size={12} /> Subir archivo de tema/extracto (PDF)
                        <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={(e) => e.target.files[0] && uploadContextoFile(e.target.files[0])} />
                    </label>
                    {assessment.contexto_filename && (
                        <a href={fileUrl(assessment.contexto_file_id)} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1.5 rounded-lg border border-edu hover:bg-edu-soft inline-flex items-center gap-1">
                            <Eye size={12} /> {assessment.contexto_filename}
                        </a>
                    )}
                </div>

                <div>
                    <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Rúbrica de evaluación</span>
                    <div className="flex flex-wrap items-center gap-2">
                        {assessment.rubric_filename ? (
                            <a href={fileUrl(assessment.rubric_file_id)} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1.5 rounded-lg border border-edu hover:bg-edu-soft inline-flex items-center gap-1">
                                <Eye size={12} /> {assessment.rubric_filename}
                            </a>
                        ) : (
                            <span className="text-xs text-edu-muted inline-flex items-center gap-1.5"><FileText size={12} /> Sin rúbrica</span>
                        )}
                        <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-edu hover:bg-edu-soft cursor-pointer">
                            <Upload size={12} /> {assessment.rubric_filename ? "Reemplazar" : "Subir rúbrica (PDF)"}
                            <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={(e) => e.target.files[0] && uploadRubric(e.target.files[0])} />
                        </label>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Tiempo por respuesta (seg)</span>
                        <input type="number" min="15" value={tiempo} onChange={(e) => setTiempo(e.target.value)} className="edu-input w-full" />
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Duración total de la entrevista (min)</span>
                        <input type="number" min="1" value={duracion} onChange={(e) => setDuracion(e.target.value)} className="edu-input w-full" />
                    </div>
                </div>
                <p className="text-xs text-edu-muted">El número de preguntas se ajusta solo según cuánto dure la entrevista, no es un número fijo.</p>

                <div className="flex justify-end">
                    <button onClick={saveConfig} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                        <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
                    </button>
                </div>
            </div>

            <div className="edu-card p-6 mb-4">
                <div className="flex items-center justify-between mb-1">
                    <h2 className="text-sm font-semibold text-edu-muted uppercase tracking-widest">Partes de la entrevista (opcional)</h2>
                    <button onClick={() => setShowNewParte(true)} className="edu-btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1.5">
                        <Plus size={13} /> Agregar parte
                    </button>
                </div>
                <p className="text-xs text-edu-muted mb-4">
                    Si no agregas ninguna parte, la IA improvisa toda la entrevista libremente sobre el tema. Si agregas
                    partes, el alumno las recorre en este orden — útil para las partes de Cambridge Speaking que necesitan
                    una imagen (ej. diferencias entre imágenes, o encontrar el intruso). Las imágenes las subes tú, la IA
                    nunca las genera.
                </p>

                {sortedPartes.length === 0 ? (
                    <p className="text-sm text-edu-muted text-center py-4">Sin partes configuradas — entrevista completamente libre.</p>
                ) : (
                    <div className="space-y-2">
                        {sortedPartes.map((p, idx) => (
                            <div key={p.id} className="border border-edu rounded-xl p-3 flex items-start gap-3">
                                {p.tipo === "imagen" && p.imagen_file_id ? (
                                    <img src={fileUrl(p.imagen_file_id)} alt={p.imagen_filename || "imagen"} className="w-14 h-14 rounded-lg object-cover border border-edu flex-shrink-0" />
                                ) : (
                                    <div className="w-14 h-14 rounded-lg bg-edu-soft flex items-center justify-center border border-edu flex-shrink-0">
                                        <Mic size={18} className="text-edu-muted" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-edu-muted uppercase tracking-widest">Parte {idx + 1} — {p.tipo === "imagen" ? "con imagen" : "libre"}</p>
                                    <p className="text-sm text-edu-body break-words">{p.instrucciones || "(sin instrucciones — IA improvisa según el tema general)"}</p>
                                </div>
                                <div className="flex flex-col gap-1 flex-shrink-0">
                                    <button onClick={() => moveParte(sortedPartes, idx, -1)} disabled={idx === 0} className="p-1 rounded border border-edu text-edu-muted hover:bg-edu-soft disabled:opacity-30">
                                        <ArrowUp size={12} />
                                    </button>
                                    <button onClick={() => moveParte(sortedPartes, idx, 1)} disabled={idx === sortedPartes.length - 1} className="p-1 rounded border border-edu text-edu-muted hover:bg-edu-soft disabled:opacity-30">
                                        <ArrowDown size={12} />
                                    </button>
                                    <button onClick={() => removeParte(p.id)} className="p-1 rounded border border-edu text-edu-muted hover:text-red-600 hover:border-red-300">
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="edu-card p-4 mb-4 bg-edu-soft/50 flex items-start gap-2 text-xs text-edu-muted">
                <Info size={14} className="flex-shrink-0 mt-0.5" />
                La IA solo hace preguntas de práctica basadas en este tema y comenta la sesión al final según tu rúbrica — nunca pone nota. La calificación final siempre la das tú.
            </div>

            {showNewParte && (
                <NewParteModal
                    assessmentId={assessmentId}
                    onClose={() => setShowNewParte(false)}
                    onCreated={() => { setShowNewParte(false); load(); }}
                />
            )}

            <h2 className="text-sm font-semibold text-edu-muted uppercase tracking-widest mb-3">Sesiones de práctica de alumnos ({sessions.length})</h2>
            {sessions.length === 0 ? (
                <div className="edu-card p-8 text-center text-edu-muted">Todavía no hay alumnos que hayan practicado.</div>
            ) : (
                <div className="space-y-2">
                    {sessions.map((s) => (
                        <button
                            key={s.id}
                            onClick={() => navigate(`/profesor/evaluacion-oral/sesion/${s.id}`)}
                            className="edu-card w-full text-left p-4 hover:bg-edu-soft transition-colors flex items-center justify-between gap-3"
                        >
                            <div>
                                <p className="font-medium text-sm">{s.student_name}</p>
                                <p className="text-xs text-edu-muted">{s.status === "closed" ? "Sesión completa" : "En progreso"} · {(s.rounds || []).length} rondas</p>
                                {s.grammar_report && (
                                    <p className="text-xs text-violet-700 mt-0.5">{s.grammar_report.error_count} error(es) de gramática detectados</p>
                                )}
                            </div>
                            <ChevronRight size={16} className="text-edu-muted" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function NewParteModal({ assessmentId, onClose, onCreated }) {
    const [tipo, setTipo] = useState("libre");
    const [instrucciones, setInstrucciones] = useState("");
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    async function create() {
        if (tipo === "imagen" && !file) {
            toast.error("Sube una imagen para esta parte");
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("tipo", tipo);
            if (instrucciones.trim()) fd.append("instrucciones", instrucciones.trim());
            if (file) fd.append("file", file);
            await api.post(`/oral-assessments/${assessmentId}/partes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Parte agregada");
            onCreated();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error agregando la parte");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="edu-card p-6 max-w-md w-full">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Agregar parte</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Tipo</span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setTipo("libre")}
                                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${tipo === "libre" ? "border-edu-navy bg-edu-navy text-white" : "border-edu text-edu-body"}`}
                            >
                                Libre
                            </button>
                            <button
                                onClick={() => setTipo("imagen")}
                                className={`flex-1 px-3 py-2 rounded-lg border text-sm inline-flex items-center justify-center gap-1.5 ${tipo === "imagen" ? "border-edu-navy bg-edu-navy text-white" : "border-edu text-edu-body"}`}
                            >
                                <ImageIcon size={13} /> Con imagen
                            </button>
                        </div>
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">
                            {tipo === "imagen" ? "Qué debe pedirle la IA al alumno sobre la imagen" : "Enfoque de esta parte (opcional)"}
                        </span>
                        <textarea
                            value={instrucciones}
                            onChange={(e) => setInstrucciones(e.target.value)}
                            rows={3}
                            className="edu-input w-full"
                            placeholder={tipo === "imagen" ? "Ej: pídele que describa las diferencias entre las dos imágenes" : "Ej: pregúntale sobre su familia y su casa"}
                        />
                    </div>
                    {tipo === "imagen" && (
                        <div>
                            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Imagen</span>
                            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="edu-input w-full" />
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-3 mt-5">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                    <button onClick={create} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm disabled:opacity-50">{saving ? "Agregando…" : "Agregar"}</button>
                </div>
            </div>
        </div>
    );
}
